import {bbox} from '@turf/turf';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {FewShotExemplarFlow, RunExemplars} from 'app/components/AgentExplorer/fewShotExemplarTypes';
import {ANALYZE_POLYGON_POPUP_WIDTH} from 'app/components/AnalyzePolygonPopup/AnalyzePolygonPopup';
import AnalyzePolygonTool from 'app/components/AnalyzePolygonPopup/AnalyzePolygonTool';
import ConnectedMap from 'app/components/ConnectedMap';
import {RenderMapCallbackOpts} from 'app/components/ConnectedMap/view';
import AddNoteControl from 'app/components/DeclarativeMap/AddNoteControl';
import DraggableLegendWithSlider from 'app/components/DeclarativeMap/DraggableLegendWidthSlider';
import DrawLayer, {EDIT_RECTANGLE_FEATURE_ID} from 'app/components/DeclarativeMap/DrawLayer';
import FeatureCollectionVector from 'app/components/DeclarativeMap/FeatureCollectionVector';
import FeatureRaster from 'app/components/DeclarativeMap/FeatureRaster';
import GeoJsonFitter from 'app/components/DeclarativeMap/GeoJsonFitter';
import GeoJsonLayersVector from 'app/components/DeclarativeMap/GeoJsonLayersVector';
import GeometryOverlaysContent from 'app/components/DeclarativeMap/GeometryOverlaysContent';
import {CursorType} from 'app/components/DeclarativeMap/MapCursorProvider';
import MapSettingsControl from 'app/components/DeclarativeMap/MapSettingsControl';
import MapShowPropertyBoundariesControl from 'app/components/DeclarativeMap/MapShowPropertyBoundariesControl';
import MapStyle from 'app/components/DeclarativeMap/MapStyle';
import MapTilesLoadingControl from 'app/components/DeclarativeMap/MapTilesLoadingControl';
import MeasureToolButton from 'app/components/DeclarativeMap/MeasureToolButton';
import NoteVector from 'app/components/DeclarativeMap/NoteVector';
import PixelInfo from 'app/components/DeclarativeMap/PixelInspector/PixelInfo';
import QuickMapVectorLayers from 'app/components/DeclarativeMap/QuickMapVectorLayers';
import {QuickMapVectorLayer} from 'app/components/DeclarativeMap/QuickMapVectorLayers';
import ShareControl from 'app/components/DeclarativeMap/ShareControl';
import TerrainControl from 'app/components/DeclarativeMap/TerrainControl';
import TileCostDebugger from 'app/components/DeclarativeMap/TileCostDebugger';
import ZoomCenterControl from 'app/components/DeclarativeMap/ZoomCenterControl';
import EditNoteGeometry from 'app/components/EditNoteGeometry';
import TimelineRaster from 'app/components/ImageryTimeline/TimelineRaster';
import {ImageryTimelineState} from 'app/components/ImageryTimeline/useImageryTimeline';
import ImageryTimelineButton from 'app/components/ImageryTimelineButton';
import {OrderImageryState} from 'app/components/OrderImagerySidebar';
import {SidebarViewState} from 'app/components/TabbedSidebar/TabbedSidebar';
import {ApiFeature, ApiFeatureData} from 'app/modules/Remote/Feature';
import {
  ApiFeatureCollection,
  GeometryOverlaySetting,
  HydratedFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {
  ApiOrganization,
  ApiOrganizationUser,
  MeasurementSystem,
} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {PolygonDispatch, PolygonState} from 'app/providers/MapPolygonStateProvider';
import {NotesActions, NotesState, StateApiNote} from 'app/stores/NotesStore';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import featureFlags from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import {BBox2d} from 'app/utils/geoJsonUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';
import * as layers from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {Mode} from 'app/utils/mapUtils/modes';

import FewShotExemplarLayer from './FewShotExemplarLayer';
import MapContextMenu from './MapContextMenu';
import {ImageRefIndex} from './MapStateProvider';
import MiniMapControl from './MiniMapControl';
import PropertyGrid from './PropertyGrid';
import GridReviewTools from './PropertyGrid/GridReviewTools';
import GridToolButton from './PropertyGrid/GridToolButton';
import {promptForNoteCoordinates} from './utils';
import {MapInteractionTool, MapSettingsTool} from './view';

interface Props {
  firebaseToken: string;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organization: I.ImmutableOf<ApiOrganization>;
  currentFeatureGeometry: ApiFeature['geometry'];

  selectedFeatureCollection: HydratedFeatureCollection;
  selectedFeatures: I.Set<I.ImmutableOf<ApiFeature>>;
  setSelectedFeatureIds: (featureLensIds: I.Set<string>) => void;

  imageRefs: mapUtils.MapImageRefs;
  featureData: I.ImmutableListOf<ApiFeatureData> | null;

  mode: Mode;
  activeMapInteractionTool: MapInteractionTool | null;
  setActiveMapInteractionTool: (t: MapInteractionTool | null) => void;
  activeMapSettingsTool: MapSettingsTool | null;
  setActiveMapSettingsTool: (t: MapSettingsTool | null) => void;
  focusedGeometry: geojson.GeoJSON | null;
  animationDuration: number;
  zoomToGeometry: (geometry: geojson.GeoJSON | null) => void;
  defaultCameraOptions: {
    center: mapboxgl.LngLat;
    pitch: number;
    bearing: number;
    zoom: number;
  } | null;

  notesState: NotesState;
  notesActions: NotesActions;
  showNotes: boolean;
  onClickNoteMarker: (g: geojson.GeoJSON | null, n: StateApiNote | undefined) => void;

  // For the on-map new-note dialog, which reaches page-level actions the map
  // doesn't otherwise touch.
  onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => unknown;
  setTags: LoggedInUserActions['setOrganizationTags'];

  polygonState: PolygonState;
  polygonDispatch: PolygonDispatch;
  openAnalyzePolygonPopup: (f: geojson.Feature<geojson.Polygon | geojson.MultiPolygon>) => void;

  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
  geoJsonFeaturesLoader: GeoJsonFeaturesLoader;
  isOverlayVisibleByName: Record<string, boolean>;
  overlaySettings: GeometryOverlaySetting[];

  mapPaddingOptions: mapboxgl.PaddingOptions;
  mapStyle: mapUtils.MapStyle;
  onMapMoved?: (e: mapboxgl.MapEvent) => void;
  measurementSystem?: MeasurementSystem;

  filterNotesCallback?: (note: StateApiNote) => boolean;

  /** Show every agent observation outline at once (purple) while reviewing a run. */
  showAllAgentObservations?: boolean;

  showCostDebugger: boolean;
  setShowCostDebugger: (b: boolean) => void;

  orderImageryState: OrderImageryState;
  setOrderImageryState: (s: React.SetStateAction<OrderImageryState>) => void;

  tabbedSidebarState: SidebarViewState;

  fewShotFlow: FewShotExemplarFlow;

  /** The open run's boxes, drawn read-only; only the polarities toggled on in RunView. */
  runExemplars: RunExemplars | null;

  quickMapVectorLayers: QuickMapVectorLayer[];

  /**
   * Called with the mapboxgl.Map instance once it's available, and again with
   * null when it unmounts. Used by sibling UI (e.g. ManageLayersDialog's
   * property-color-rule editor) that needs to query source features.
   */
  onMapInit?: (map: mapboxgl.Map | null) => void;

  /** Feature ID to hover from outside the map (e.g. the multi-location list panel). */
  externalHoveredFeatureId?: number | null;

  imageryTimelineDisplayed: ImageryTimelineState['displayed'];
  onCloseImageryTimeline: () => void;
}

const SyncMapRef: React.FunctionComponent<{
  map: mapboxgl.Map;
  onMapInit?: (map: mapboxgl.Map | null) => void;
}> = ({map, onMapInit}) => {
  React.useEffect(() => {
    onMapInit?.(map);
    return () => {
      onMapInit?.(null);
    };
  }, [map, onMapInit]);
  return null;
};

const DEFAULT_CONTROL_POSITION = 'top-right';

// Matches mapbox-gl-draw's default active style, so a merged area looks like the rectangles it
// was drawn from.
const ORDER_SUBSET_LAYER_CONFIGS: React.ComponentProps<typeof GeoJsonLayersVector>['layerConfigs'] =
  [
    {type: 'fill', layout: {}, paint: {'fill-color': '#fbb03b', 'fill-opacity': 0.1}},
    {type: 'line', layout: {}, paint: {'line-color': '#fbb03b', 'line-width': 2}},
  ];

const Map: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  firebaseToken,
  profile,
  organization,
  currentFeatureGeometry,
  selectedFeatureCollection,
  selectedFeatures,
  setSelectedFeatureIds,
  imageRefs,
  featureData,
  mode,
  activeMapInteractionTool,
  setActiveMapInteractionTool,
  activeMapSettingsTool,
  setActiveMapSettingsTool,
  focusedGeometry,
  animationDuration,
  zoomToGeometry,
  notesState,
  notesActions,
  showNotes,
  onClickNoteMarker,
  onSelectImageRefs,
  setTags,
  polygonState,
  polygonDispatch,
  openAnalyzePolygonPopup,
  overlayFeatureCollections,
  geoJsonFeaturesLoader,
  isOverlayVisibleByName,
  overlaySettings,
  mapPaddingOptions,
  mapStyle,
  onMapMoved,
  defaultCameraOptions,
  filterNotesCallback,
  showAllAgentObservations,
  showCostDebugger,
  setShowCostDebugger,
  orderImageryState,
  setOrderImageryState,
  tabbedSidebarState,
  fewShotFlow,
  runExemplars,
  measurementSystem,
  quickMapVectorLayers,
  onMapInit,
  externalHoveredFeatureId,
  imageryTimelineDisplayed,
  onCloseImageryTimeline,
}) => {
  const {overlayMask} = polygonState;

  const selectedFeatureIds = React.useMemo(
    () => selectedFeatures.map((f) => f!.get('id')).toSet(),
    [selectedFeatures]
  );

  const isAgentTabOpen =
    tabbedSidebarState.isExpanded && tabbedSidebarState.currentViewTitle === 'Agent';
  const {
    exemplars: fewShotExemplars,
    negativeExemplars: fewShotNegativeExemplars,
    editing: fewShotEditing,
  } = fewShotFlow.fewShotState;
  const fewShotEditingFeature = React.useMemo<geojson.Feature | null>(() => {
    const exemplar =
      fewShotEditing === null
        ? null
        : (fewShotEditing.polarity === 'positive' ? fewShotExemplars : fewShotNegativeExemplars)[
            fewShotEditing.index
          ];
    return exemplar
      ? {type: 'Feature', id: EDIT_RECTANGLE_FEATURE_ID, geometry: exemplar, properties: {}}
      : null;
  }, [fewShotExemplars, fewShotNegativeExemplars, fewShotEditing]);

  const firstLayerKey = imageRefs[0].layerKey;

  const fitBoundsOptions = {
    padding: mapUtils.insetPadding(mapPaddingOptions, 10),
    maxZoom:
      layerUtils.isLayerKeyHighResTruecolor(firstLayerKey) || firstLayerKey === layers.NONE
        ? 18
        : 15,
    duration: animationDuration,
  };

  const currentFeatureBounds = React.useMemo(() => {
    if (selectedFeatures.isEmpty()) {
      return null;
    } else {
      // as any because there’s some disconnect between the geojson types and
      // the turf types around GeometryCollection.
      return bbox(geoJsonUtils.featureCollection(selectedFeatures.toJS()) as any) as BBox2d;
    }
  }, [selectedFeatures]);

  const [showTerrain, setShowTerrain] = React.useState(false);

  const [showPropertyBoundaries, setShowPropertyBoundaries] = React.useState(true);

  // Given some internal mapbox-gl race conditions, we want to ensure that any
  // unmounting behavior that happens as a result of updating the active tool
  // happens before we set the terrain property on the map style.
  const toggleShowTerrain = React.useCallback(() => {
    setShowTerrain((prevShowTerrain) => {
      const nextShowTerrain = !prevShowTerrain;

      // Close measure distance tool when switching to 3D viewpoint.
      if (nextShowTerrain && activeMapInteractionTool === 'measureDistance') {
        setActiveMapInteractionTool(null);
      }

      recordEvent(nextShowTerrain ? 'Entered 3D mode' : 'Exited 3D mode');

      return nextShowTerrain;
    });
  }, [activeMapInteractionTool, setActiveMapInteractionTool]);

  const setSubsetFeature = React.useCallback(
    (f: geojson.Feature | null) =>
      setOrderImageryState((oldState) => {
        // We shouldn’t get non-polygons but just to be safe
        if (f === oldState.subsetFeature || (f && f.geometry.type !== 'Polygon')) {
          return oldState;
        }
        const rectangle = f as geojson.Feature<geojson.Polygon> | null;

        if (!oldState.isAddingSubsetRectangle) {
          return {...oldState, subsetFeature: rectangle};
        }
        // Backspace and other clears while adding are about the rectangle in progress, not the
        // ones already placed.
        if (!rectangle) {
          return oldState;
        }
        // Union rather than a plain MultiPolygon: overlapping rectangles would otherwise count
        // their shared ground twice in the acreage.
        const merged = oldState.subsetFeature
          ? featureUtils.compileGeometry([oldState.subsetFeature.geometry, rectangle.geometry])
          : null;
        return {
          ...oldState,
          isAddingSubsetRectangle: false,
          subsetFeature: {
            ...rectangle,
            geometry: merged ?? rectangle.geometry,
          },
        };
      }),
    [setOrderImageryState]
  );

  // The rectangle edit mode can only hold one four-cornered shape, so a merged area is shown as a
  // plain layer instead, and so is the placed area while another rectangle is being drawn.
  const subsetFeature = orderImageryState.subsetFeature;
  const isSubsetInDrawLayer =
    !orderImageryState.isAddingSubsetRectangle &&
    geoJsonUtils.isRectanglePolygon(subsetFeature?.geometry);

  const onSelectOverlayForNote = React.useCallback(
    (geometry: geojson.Geometry) => {
      const feature = geoJsonUtils.feature(geometry, {}, {id: 'geometry-from-overlay'});
      notesActions.setPendingNoteGeometryFeature(feature);
      notesActions.stopEditingPendingGeometry();
    },
    [notesActions]
  );

  const clickHandlers = React.useMemo(() => {
    return {
      [CONSTANTS.PRODUCT_USER_POINT]: (f) => {
        const url = f.getIn(['properties', 'url']);
        if (url) {
          window.open(url, '_blank');
        }
      },
    };
  }, []);

  let cursor: CursorType | undefined = undefined;
  if (
    notesState.addPendingNoteGeometryMode === 'point' ||
    activeMapInteractionTool === 'pixelInspector' ||
    activeMapInteractionTool === 'measureDistance'
  ) {
    cursor = 'crosshair';
  } else if (
    notesState.addPendingNoteGeometryMode === 'selectOverlay' ||
    polygonState.analyzeAreaDrawMode === 'selectOverlayPolygon'
  ) {
    cursor = 'pointer';
  }

  return (
    <>
      <ConnectedMap
        imageRefs={imageRefs}
        featureData={featureData}
        mode={mode}
        globalCursor={cursor}
        firebaseToken={firebaseToken}
        onMapMoved={onMapMoved}
        onMapClick={(event: mapboxgl.MapMouseEvent) => {
          // If we are locating a pending note, update the pending note geometry with
          // the selected coordinates, then exit.
          //
          // TODO(fiona): Handle this in a mapbox-gl-draw mode
          if (notesState.addPendingNoteGeometryMode === 'point') {
            event.preventDefault();

            const {lng, lat} = event.lngLat;

            const feature = geoJsonUtils.point([lng, lat], {}, {id: 'point-from-click'});
            notesActions.setPendingNoteGeometryFeature(feature);
            notesActions.stopEditingPendingGeometry();
          }
        }}
        selectedFeatureCollection={selectedFeatureCollection}
        selectedFeatureIds={selectedFeatureIds}
        selectedFeatures={selectedFeatures}
        moveCompareSliderToOnlyShowLatestImage={
          // The on-map note dialog is anchored to the leader map, which compare
          // mode clips to the right of the slider.
          !!notesState.noteComposer ||
          notesState.addPendingNoteGeometryMode === 'rect' ||
          notesState.addPendingNoteGeometryMode === 'polygon' ||
          (activeMapInteractionTool === 'analyzePolygon' &&
            !notesState.pendingNoteGeometryFeature) ||
          activeMapInteractionTool === 'measureArea' ||
          activeMapInteractionTool === 'measureDistance'
        }
        compareFullScreenOffset={
          // Offset by the width of the AnalyzePolygonPopup plus 20 pixels margin
          activeMapInteractionTool === 'analyzePolygon'
            ? ANALYZE_POLYGON_POPUP_WIDTH + 20
            : undefined
        }
        controlPosition={DEFAULT_CONTROL_POSITION}
        controlPadding={mapPaddingOptions}
        defaultCameraOptions={defaultCameraOptions}
        hideNavigationControl
        measurementSystem={measurementSystem}
        organization={organization}
        tabbedSidebarIsExpanded={tabbedSidebarState.isExpanded}
      >
        {({map, isMapLoaded, index, cursor, layerKey, isCameraLeader}: RenderMapCallbackOpts) => {
          // If overlayMask is available (it’s typically maintained by
          // MapPolygonStateProvider) then we defer to it for the current
          // cursor, since it won’t update its cursor value until the mask is
          // done.
          //
          // This lets us wait until the mask is calculated for the new
          // cursor before switching to it, which prevents a flash of either an
          // unmasked layer or the old mask applied to the new layer.

          // We check overlayMask’s existence as well as cursorIdx here, so
          // it’s safe to use "as" and "!" below.
          const useMask =
            overlayMask &&
            overlayMask.cursors.length === imageRefs.length &&
            overlayMask.layerKey === layerKey;

          const layer = layerUtils.getLayer(layerKey);

          return (
            <MapInteractionProvider
              features={selectedFeatureCollection.get('features')}
              selectedFeatureIds={selectedFeatureIds}
              setSelectedFeatureIds={setSelectedFeatureIds}
              externalHoveredFeatureIds={
                externalHoveredFeatureId ? [externalHoveredFeatureId] : null
              }
            >
              {({
                selectFeatureById,
                hoveredFeatureIds,
                hoverFeatureById,
                externalHoveredFeatureIds,
              }) => (
                <>
                  <SyncMapRef map={map} onMapInit={onMapInit} />
                  {notesState.addPendingNoteGeometryMode === 'point' && (
                    <mapUtils.DisableClickableFeatures />
                  )}
                  {/* Camera-driving control: render only on the camera leader.
                      In compare mode the before-map follows it via CompareMap's
                      one-directional sync. */}
                  {isCameraLeader && (
                    <ZoomCenterControl
                      map={map}
                      isMapLoaded={isMapLoaded}
                      featureBounds={currentFeatureBounds}
                      zoomToGeometry={zoomToGeometry}
                      fitBoundsOptions={fitBoundsOptions}
                      additionalFitButtons={[<GridToolButton key="grid" />]}
                    />
                  )}
                  <TerrainControl
                    map={map}
                    isMapLoaded={isMapLoaded}
                    showTerrain={showTerrain}
                    toggleShowTerrain={toggleShowTerrain}
                  />
                  {/* Both stay mounted so each keeps its place in the layer stack. */}
                  <FeatureRaster
                    map={map}
                    isMapLoaded={isMapLoaded}
                    activeLayerKey={layerKey}
                    cursor={
                      imageryTimelineDisplayed
                        ? null
                        : useMask
                          ? overlayMask!.cursors[index]
                          : cursor
                    }
                    featureCollection={selectedFeatureCollection}
                    featureData={featureData || I.List([])}
                    mask={useMask ? overlayMask!.masks[index] : null}
                  />
                  <TimelineRaster
                    map={map}
                    isMapLoaded={isMapLoaded}
                    frame={
                      imageryTimelineDisplayed && {
                        imageUrl: imageryTimelineDisplayed.image.src,
                        bounds: imageryTimelineDisplayed.frame.bounds,
                      }
                    }
                  />
                  <FeatureCollectionVector
                    map={map}
                    isMapLoaded={isMapLoaded}
                    featureCollection={selectedFeatureCollection}
                    selectedFeatureIds={selectedFeatureIds}
                    selectFeatureById={selectFeatureById}
                    hoveredFeatureIds={hoveredFeatureIds}
                    hoverFeatureById={hoverFeatureById}
                    showPropertyBoundaries={showPropertyBoundaries}
                    externalHoveredFeatureIds={externalHoveredFeatureIds}
                    mode="focus"
                  />
                  <GeometryOverlaysContent
                    map={map}
                    isMapLoaded={isMapLoaded}
                    selectedFeatures={selectedFeatures}
                    overlayFeatureCollections={overlayFeatureCollections}
                    geoJsonFeaturesLoader={geoJsonFeaturesLoader}
                    isOverlayVisibleByName={isOverlayVisibleByName}
                    overlaySettings={overlaySettings}
                    openAnalyzePolygonPopup={openAnalyzePolygonPopup}
                    clickHandlers={clickHandlers}
                    readyToSelectOverlayPolygon={
                      polygonState.analyzeAreaDrawMode === 'selectOverlayPolygon'
                    }
                    readyToSelectOverlayForNote={
                      notesState.addPendingNoteGeometryMode === 'selectOverlay'
                    }
                    onSelectOverlayForNote={onSelectOverlayForNote}
                    onOverlaySelectionError={(msg) => notesActions.setOverlayGeometryError(msg)}
                  />
                  <QuickMapVectorLayers
                    map={map}
                    isMapLoaded={isMapLoaded}
                    layers={quickMapVectorLayers}
                  />
                  {showCostDebugger && (
                    <TileCostDebugger
                      map={map}
                      isMapLoaded={isMapLoaded}
                      features={selectedFeatures}
                    />
                  )}
                  {/* Camera-driving: fit only the leader; the follower mirrors
                      it via CompareMap's one-directional sync. Fitting both maps
                      directly would re-introduce the animation fighting. */}
                  {isCameraLeader && (
                    <GeoJsonFitter
                      map={map}
                      geoJson={focusedGeometry}
                      fitBoundsOptions={fitBoundsOptions}
                      geoJsonComparison="shallow"
                    />
                  )}
                  <MapStyle map={map} style={mapStyle} />

                  <MapSettingsControl
                    map={map}
                    isMapLoaded={isMapLoaded}
                    position={DEFAULT_CONTROL_POSITION}
                    activeMapSettingsTool={activeMapSettingsTool}
                    setActiveMapSettingsTool={setActiveMapSettingsTool}
                    profile={profile}
                    showCostDebugger={showCostDebugger}
                    setShowCostDebugger={setShowCostDebugger}
                    startsGroup
                  />
                  {featureData && (
                    <DraggableLegendWithSlider
                      imageRefs={imageRefs}
                      layer={layer}
                      index={index}
                      offset={{left: mapPaddingOptions.left}}
                      firebaseToken={firebaseToken}
                      featureData={featureData}
                      featureCollection={selectedFeatureCollection}
                      map={map}
                      polygonDispatch={polygonDispatch}
                      polygon={
                        currentFeatureGeometry as geojson.Polygon | geojson.MultiPolygon | null
                      }
                      overlayMask={overlayMask}
                    />
                  )}
                  <MapShowPropertyBoundariesControl
                    map={map}
                    isMapLoaded={isMapLoaded}
                    position={DEFAULT_CONTROL_POSITION}
                    showPropertyBoundaries={showPropertyBoundaries}
                    setShowPropertyBoundaries={setShowPropertyBoundaries}
                  />
                  <PropertyGrid map={map} isMapLoaded={isMapLoaded} isPrimaryMap={isCameraLeader} />
                  <MapTilesLoadingControl map={map} isMapLoaded={isMapLoaded} />
                  {/* Tool stack order: view, then what renders, then actions. */}
                  {/* Notes belong to a single feature, so hide this while more
                      than one property is selected. */}
                  {isCameraLeader && selectedFeatures.size === 1 && (
                    <AddNoteControl
                      map={map}
                      isMapLoaded={isMapLoaded}
                      position={DEFAULT_CONTROL_POSITION}
                      startsGroup
                      notesState={notesState}
                      notesActions={notesActions}
                      firebaseToken={firebaseToken}
                      organization={organization}
                      profile={profile}
                      featureCollection={featureCollectionUtils.hydratedFeatureCollectionAsApiFeatureCollection(
                        selectedFeatureCollection
                      )}
                      featureData={featureData}
                      imageRefs={imageRefs}
                      isOverlayVisibleByName={isOverlayVisibleByName}
                      onSelectImageRefs={onSelectImageRefs}
                      setTags={setTags}
                      promptForCoordinates={() =>
                        promptForNoteCoordinates(notesActions, zoomToGeometry)
                      }
                      zoomToGeometry={zoomToGeometry}
                    />
                  )}
                  <MeasureToolButton
                    imageRefs={imageRefs}
                    notesState={notesState}
                    notesActions={notesActions}
                    map={map}
                    isMapLoaded={isMapLoaded}
                    position={DEFAULT_CONTROL_POSITION}
                    isOpen={
                      activeMapInteractionTool === 'measureDistance' ||
                      activeMapInteractionTool === 'measureArea'
                    }
                    setIsOpen={(isOpen) =>
                      setActiveMapInteractionTool(isOpen ? 'measureDistance' : null)
                    }
                    disabled={showTerrain}
                    measurementSystem={measurementSystem}
                    polygonDispatch={polygonDispatch}
                    polygonState={polygonState}
                    activeMapInteractionTool={activeMapInteractionTool}
                    setActiveMapInteractionTool={setActiveMapInteractionTool}
                    mapIndex={index as ImageRefIndex}
                  />
                  {isCameraLeader && featureFlags.IMAGERY_TIMELINE(profile) && (
                    <ImageryTimelineButton
                      map={map}
                      isMapLoaded={isMapLoaded}
                      position={DEFAULT_CONTROL_POSITION}
                      isOpen={activeMapInteractionTool === 'imageryTimeline'}
                      setIsOpen={(isOpen) => {
                        if (isOpen) {
                          setActiveMapInteractionTool('imageryTimeline');
                          recordEvent('Opened imagery timeline');
                        } else {
                          onCloseImageryTimeline();
                        }
                      }}
                      disabled={
                        selectedFeatures.size !== 1 ||
                        layerKey === layers.NONE ||
                        !featureData?.some((d) => !!d!.getIn(['images', 'urls', layerKey]))
                      }
                    />
                  )}
                  {isCameraLeader &&
                    notesState.mayCreateNotes &&
                    !notesState.noteComposer &&
                    !notesState.addPendingNoteGeometryMode &&
                    selectedFeatures.size === 1 && (
                      <MapContextMenu map={map} notesActions={notesActions} />
                    )}
                  <PixelInfo
                    map={map}
                    isMapLoaded={isMapLoaded}
                    position={DEFAULT_CONTROL_POSITION}
                    cursor={cursor}
                    firebaseToken={firebaseToken}
                    featureData={featureData}
                    polygon={currentFeatureGeometry}
                    activeLayerKey={layerKey}
                    featureCollection={selectedFeatureCollection}
                    disabled={!!overlayMask}
                    panelIndex={index}
                  />
                  <ShareControl
                    map={map}
                    isMapLoaded={isMapLoaded}
                    position={DEFAULT_CONTROL_POSITION}
                    imageRefs={imageRefs}
                  />
                  <AnalyzePolygonTool
                    map={map}
                    isMapLoaded={isMapLoaded}
                    polygonState={polygonState}
                    polygonDispatch={polygonDispatch}
                    preventDrawLayer={
                      notesState.addPendingNoteGeometryMode === 'polygon' ||
                      notesState.addPendingNoteGeometryMode === 'rect'
                    }
                    drawingMode={
                      polygonState.analyzeAreaDrawMode === 'drawRectangle'
                        ? 'draw_rectangle'
                        : 'draw_polygon'
                    }
                    isOpen={
                      activeMapInteractionTool === 'analyzePolygon' ||
                      activeMapInteractionTool === 'measureArea'
                    }
                  />

                  {tabbedSidebarState.isExpanded &&
                    tabbedSidebarState.currentViewTitle === 'Order' &&
                    orderImageryState.orderSubset &&
                    activeMapInteractionTool !== 'analyzePolygon' && (
                      <>
                        <DrawLayer
                          map={map}
                          isMapLoaded={isMapLoaded}
                          // A merged area is done being drawn until the user asks to add to it,
                          // so the draw control leaves the map rather than replacing it on the
                          // next drag.
                          visible={
                            !subsetFeature ||
                            isSubsetInDrawLayer ||
                            orderImageryState.isAddingSubsetRectangle
                          }
                          drawingMode="draw_rectangle"
                          editRectangle={true}
                          feature={isSubsetInDrawLayer ? subsetFeature : null}
                          setFeature={setSubsetFeature}
                        />
                        {subsetFeature && !isSubsetInDrawLayer && (
                          <GeoJsonLayersVector
                            idPrefix="orderSubset"
                            map={map}
                            isMapLoaded={isMapLoaded}
                            geoJson={subsetFeature}
                            layerConfigs={ORDER_SUBSET_LAYER_CONFIGS}
                          />
                        )}
                      </>
                    )}
                  {isAgentTabOpen && fewShotFlow.fewShotState.drawing && (
                    // Remounted per exemplar (key) so each finished rectangle drops out of the
                    // draw control's own store once it's captured as an exemplar below, instead
                    // of piling up drawn-but-uncleared shapes underneath it. Keyed on both lists
                    // since a box can land in either.
                    <DrawLayer
                      key={fewShotExemplars.length + fewShotNegativeExemplars.length}
                      map={map}
                      isMapLoaded={isMapLoaded}
                      visible={true}
                      drawingMode="draw_rectangle"
                      feature={null}
                      setFeature={(f) => {
                        if (f && f.geometry.type === 'Polygon') {
                          fewShotFlow.addExemplar(f.geometry);
                        }
                      }}
                    />
                  )}
                  {isAgentTabOpen && fewShotEditing !== null && fewShotEditingFeature && (
                    <DrawLayer
                      key={`${fewShotEditing.polarity}-${fewShotEditing.index}`}
                      map={map}
                      isMapLoaded={isMapLoaded}
                      visible={true}
                      drawingMode="simple_select"
                      editRectangle={true}
                      feature={fewShotEditingFeature}
                      setFeature={(f) => {
                        if (!f) {
                          fewShotFlow.removeExemplar(fewShotEditing);
                        } else if (f.geometry.type === 'Polygon') {
                          fewShotFlow.updateExemplar(fewShotEditing, f.geometry);
                        }
                      }}
                      onDeselectFeature={() =>
                        fewShotFlow.stopEditingExemplar(fewShotEditing, true)
                      }
                    />
                  )}
                  {isAgentTabOpen && runExemplars && (
                    <FewShotExemplarLayer
                      map={map}
                      isMapLoaded={isMapLoaded}
                      readOnly
                      idSuffix="run"
                      exemplars={runExemplars.positives}
                      negativeExemplars={runExemplars.negatives}
                    />
                  )}
                  {isAgentTabOpen && (
                    <FewShotExemplarLayer
                      map={map}
                      isMapLoaded={isMapLoaded}
                      exemplars={fewShotExemplars}
                      negativeExemplars={fewShotNegativeExemplars}
                      problems={fewShotFlow.exemplarProblems}
                      hovered={fewShotFlow.fewShotState.hovered}
                      onHoverExemplar={fewShotFlow.setHoveredExemplar}
                      editing={fewShotEditing}
                      onClickExemplar={
                        fewShotFlow.fewShotState.drawing ? null : fewShotFlow.startEditingExemplar
                      }
                    />
                  )}
                  <MiniMapControl
                    map={map}
                    isMapLoaded={isMapLoaded}
                    selectedFeatureIds={selectedFeatureIds}
                    featureCollection={selectedFeatureCollection}
                    selectFeatureById={selectFeatureById}
                    setSelectedFeatureLensIds={setSelectedFeatureIds}
                    hoveredFeatureId={hoveredFeatureIds.first() ?? null}
                    hoverFeatureById={hoverFeatureById}
                  />
                  <NoteVector
                    map={map}
                    notesState={notesState}
                    notesActions={notesActions}
                    isMapLoaded={isMapLoaded}
                    isVisible={showNotes}
                    onClickNoteMarkerCallback={onClickNoteMarker}
                    filterNotesCallback={filterNotesCallback}
                    showAllAgentObservations={showAllAgentObservations}
                    disableClicks={
                      isAgentTabOpen &&
                      (fewShotFlow.fewShotState.drawing || fewShotEditing !== null)
                    }
                  />
                  <EditNoteGeometry
                    map={map}
                    isMapLoaded={isMapLoaded}
                    visible={showNotes}
                    notesState={notesState}
                    notesActions={notesActions}
                  />
                </>
              )}
            </MapInteractionProvider>
          );
        }}
      </ConnectedMap>
      <GridReviewTools
        isSidebarExpanded={tabbedSidebarState.isExpanded}
        activeMapInteractionTool={activeMapInteractionTool}
      />
    </>
  );
};

export default Map;

interface MapInteractionProviderValue {
  selectFeatureById: (fId: number) => void;
  hoveredFeatureIds: I.Set<number>;
  hoverFeatureById: (fId: number | null) => void;
  externalHoveredFeatureIds: I.Set<number>;
}

/**
 * Provider to manage hovering and clicking for the FeatureCollectionVector and
 * MiniMapControl.
 *
 * Translates hovers and clicks appropriately for multi-feature properties. For
 * example, clicking on a part of a multi-feature property will select the whole
 * of it, unless you’re switching from another piece of that same property.
 *
 * Likewise, hovering will match the same behavior as clicking.
 */
export const MapInteractionProvider: React.FunctionComponent<{
  features: I.ImmutableOf<ApiFeature[]>;
  selectedFeatureIds: I.Set<number>;
  setSelectedFeatureIds: (lensIds: I.Set<string>) => void;
  externalHoveredFeatureIds?: number[] | null;
  children: (val: MapInteractionProviderValue) => React.ReactElement;
}> = ({
  features,
  selectedFeatureIds,
  setSelectedFeatureIds,
  externalHoveredFeatureIds,
  children,
}) => {
  const featuresById = React.useMemo(
    () =>
      features
        .groupBy((f) => f!.get('id'))
        // ids are unique so we know we’ll have exactly 1 per group
        .map((a) => a!.first())
        .toMap(),
    [features]
  );

  const featuresByName = React.useMemo(
    () => features.groupBy((f) => f!.getIn(['properties', 'name'])).toMap(),
    [features]
  );

  const selectedFeatureName = !selectedFeatureIds.isEmpty()
    ? featuresById.get(selectedFeatureIds.first()).getIn(['properties', 'name'])
    : null;

  const [hoveredFeatureIds, setHoveredFeatureIds] = useStateWithDeps<I.Set<number>>(I.Set(), [
    selectedFeatureIds,
  ]);

  const selectFeatureById = React.useCallback(
    (id: number) => {
      const feature = featuresById.get(id);
      const newSelectedFeatureName = feature.getIn(['properties', 'name']);

      if (selectedFeatureName === newSelectedFeatureName) {
        // If the new feature has the same name as the old, only select the new
        // feature. We’re navigating among a multi-feature property.
        setSelectedFeatureIds(I.Set([feature.getIn(['properties', 'lensId'])!]));
      } else {
        // Otherwise, select every property with the same name as the one being
        // clicked on. For single properties, that will be the one, but for
        // multi-feature properties this will lead to the zoomed out,
        // all-selected view.
        setSelectedFeatureIds(
          featuresByName
            .get(newSelectedFeatureName)
            .map((f) => f!.getIn(['properties', 'lensId'])!)
            .toSet()
        );
      }
    },
    [setSelectedFeatureIds, featuresById, featuresByName, selectedFeatureName]
  );

  const hoverFeatureById = React.useCallback(
    (hoveredFeatureId: number | null) => {
      setHoveredFeatureIds((oldHoveredFeatureIds) => {
        if (hoveredFeatureId === null) {
          return I.Set();
        }

        const hoveredFeatureName = featuresById.get(hoveredFeatureId).getIn(['properties', 'name']);

        let newHoveredFeatureIds: I.Set<number>;

        if (hoveredFeatureName === selectedFeatureName) {
          newHoveredFeatureIds = I.Set([hoveredFeatureId]);
        } else {
          newHoveredFeatureIds = featuresByName
            .get(hoveredFeatureName)
            .map((f) => f!.get('id'))
            .toSet();
        }

        // Avoid unnecessary re-rendering when moving the mouse over properties
        // by trying to maintain object equality.
        return I.is(oldHoveredFeatureIds, newHoveredFeatureIds)
          ? oldHoveredFeatureIds
          : newHoveredFeatureIds;
      });
    },
    [featuresById, featuresByName, selectedFeatureName, setHoveredFeatureIds]
  );

  const effectiveHoveredFeatureIds = React.useMemo(() => {
    if (externalHoveredFeatureIds != null) {
      return hoveredFeatureIds.union(I.Set(externalHoveredFeatureIds));
    }
    return hoveredFeatureIds;
  }, [externalHoveredFeatureIds, hoveredFeatureIds]);

  return children({
    selectFeatureById,
    hoveredFeatureIds: effectiveHoveredFeatureIds,
    hoverFeatureById,
    externalHoveredFeatureIds: I.Set(externalHoveredFeatureIds ?? []),
  });
};
